import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { BreadcrumbCurrent, BreadcrumbLink, BreadcrumbsHome, StyledBreadcrumbs } from './AppBreadcrumbs.styles';
import { NavigateNext, Home } from '@mui/icons-material';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { getProject } from '../../api/projects/requests.ts';
import { useMemo } from 'react';
import { getBatchJob } from '../../api/batch-jobs/requests.ts';
import { QueryKey } from 'api/model.ts';
import useWorkflow from 'api/workflows/useWorkflow.ts';
import useIsTruncated from 'hooks/useIsTruncated.ts';

const ignored = ['canvas', 'revision', 'execution', 'edit'];

type CrumbProps = {
  label: string;
  routeTo?: string;
};

const Crumb = ({ label, routeTo }: CrumbProps) => {
  const { ref, isTruncated } = useIsTruncated(label);

  return (
    <Tooltip title={isTruncated ? label : ''} placement="bottom" arrow enterDelay={400}>
      {routeTo ? (
        <Link to={routeTo}>
          <BreadcrumbLink ref={ref} component="span">
            {label}
          </BreadcrumbLink>
        </Link>
      ) : (
        <BreadcrumbCurrent ref={ref} color="text.primary">
          {label}
        </BreadcrumbCurrent>
      )}
    </Tooltip>
  );
};

const AppBreadcrumbs = () => {
  const { projectId, workflowId, workflowExecutionId, workflowRevision, batchJobId, batchJobExecutionId } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const pathnames = location.pathname
    .split('/')
    .filter((x) => x)
    .filter((p) => !ignored.includes(p) && p !== workflowRevision);

  const { data: project } = useQuery({
    queryFn: () => getProject(projectId ?? ''),
    queryKey: [QueryKey.Projects, { id: projectId }],
    enabled: !!projectId,
  });

  const { workflow } = useWorkflow({ projectId: projectId ?? '', workflowId: workflowId ?? '', workflowRevision });

  const { data: batchJob } = useQuery({
    queryFn: () => getBatchJob(batchJobId ?? ''),
    queryKey: [QueryKey.BatchJobs, { id: batchJobId }],
    enabled: !!batchJobId,
  });

  const idNameMap: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    if (workflow && workflowId) {
      map[workflowId] = workflow.name;
    }
    if (project && projectId) {
      map[projectId] = project.name;
    }
    if (workflowExecutionId && workflow) {
      map[workflowExecutionId] = `${workflow?.name} execution(${workflowExecutionId.slice(0, 8)}...)`;
    }
    if (batchJob && batchJobId) {
      map[batchJobId] = batchJob.name;
    }
    if (batchJobExecutionId && batchJob) {
      map[batchJobExecutionId] = `${batchJob?.name} execution(${batchJobExecutionId.slice(0, 8)}...)`;
    } else if (batchJobExecutionId) {
      map[batchJobExecutionId] = `Batch Job Iterations(${batchJobExecutionId.slice(0, 8)}...)`;
    }
    return map;
  }, [project, workflow, batchJob, projectId, workflowId, batchJobId, batchJobExecutionId, workflowExecutionId]);

  return (
    <StyledBreadcrumbs
      aria-label="breadcrumb"
      maxItems={isCompact ? 3 : 5}
      itemsBeforeCollapse={1}
      itemsAfterCollapse={2}
      separator={<NavigateNext fontSize="small" />}
    >
      <Tooltip title="Home" placement="bottom" arrow enterDelay={400}>
        <Link to="/" aria-label="Home">
          <BreadcrumbsHome component="span">
            <Home fontSize="small" />
          </BreadcrumbsHome>
        </Link>
      </Tooltip>
      {pathnames.map((name, index) => {
        const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
        const isLast = index === pathnames.length - 1;
        let renderedName = idNameMap[name] ?? name;
        if (!(name in idNameMap)) {
          renderedName = renderedName
            .replace(/-/g, ' ')
            .replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
        }

        return <Crumb key={renderedName} label={renderedName} routeTo={isLast ? undefined : routeTo} />;
      })}
    </StyledBreadcrumbs>
  );
};

export default AppBreadcrumbs;
